import {
  Directive, Input, ElementRef, TemplateRef, ViewContainerRef,
  EmbeddedViewRef, HostListener, Renderer2, ChangeDetectorRef, OnDestroy
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';

@Directive({
  selector: '[appCustomTooltip]',
  standalone: true,
})
export class CustomTooltipDirective implements OnDestroy {
  @Input('appCustomTooltip') tooltipContent: TemplateRef<any> | string | SafeHtml;
  // (optional) context for template usage
  @Input() appCustomTooltipContext: any;

  @HostListener('mouseenter') onTargetEnter() {
    this.isHoveringTarget = true;
    this.showTooltip();
  }
  @HostListener('mouseleave') onTargetLeave() {
    this.isHoveringTarget = false;
    setTimeout(() => this.checkIfShouldHide(), 100);
  }

  private tooltipViewRef?: EmbeddedViewRef<any>;
  private tooltipElement?: HTMLElement;
  private isHoveringTarget = false;
  private isHoveringTooltip = false;
  private unlistenEnter?: () => void;
  private unlistenLeave?: () => void;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly renderer: Renderer2,
    private readonly cdr: ChangeDetectorRef
  ) {}

  showTooltip() {
    if (!this.tooltipContent || this.tooltipElement) return;

    const tooltipContainer = this.renderer.createElement('div');
    this.renderer.addClass(tooltipContainer, 'mat-tooltip-container');
    this.renderer.addClass(tooltipContainer, 'custom-tooltip-animate');
    this.renderer.setStyle(tooltipContainer, 'position', 'fixed');
    this.renderer.setStyle(tooltipContainer, 'pointer-events', 'auto');
    this.renderer.setStyle(tooltipContainer, 'z-index', '1051');
    this.renderer.setStyle(tooltipContainer, 'max-width', '280px');
    this.renderer.setStyle(tooltipContainer, 'background', 'rgba(85, 91, 107, 0.9)');
    this.renderer.setStyle(tooltipContainer, 'color', '#fff');
    this.renderer.setStyle(tooltipContainer, 'border-radius', '4px');
    this.renderer.setStyle(tooltipContainer, 'padding', '8px');
    this.renderer.setStyle(tooltipContainer, 'font-size', '13px');
    this.renderer.setStyle(tooltipContainer, 'box-shadow', '0 8px 16px rgba(0,0,0,.2)');

    if (this.tooltipContent instanceof TemplateRef) {
      this.tooltipViewRef = this.viewContainerRef.createEmbeddedView(
        this.tooltipContent,
        this.appCustomTooltipContext ?? {}
      );
      this.cdr.detectChanges();
      this.tooltipViewRef.rootNodes.forEach(node => this.renderer.appendChild(tooltipContainer, node));
    } else {
      const wrapper = this.renderer.createElement('div');
      wrapper.innerHTML = this.tooltipContent;
      this.renderer.appendChild(tooltipContainer, wrapper);
    }

    this.renderer.addClass(tooltipContainer, 'custom-tooltip-show');

    this.unlistenEnter = this.renderer.listen(tooltipContainer, 'mouseenter', () => {
      this.isHoveringTooltip = true;
    });
    this.unlistenLeave = this.renderer.listen(tooltipContainer, 'mouseleave', () => {
      this.isHoveringTooltip = false;
      setTimeout(() => this.checkIfShouldHide(), 100);
    });

    document.body.appendChild(tooltipContainer);
    this.tooltipElement = tooltipContainer;

    requestAnimationFrame(() => this.positionTooltip(tooltipContainer));
  }

  hideTooltip() {
    if (this.tooltipViewRef) {
      this.tooltipViewRef.destroy();
      this.tooltipViewRef = undefined;
    }
    if (this.tooltipElement?.parentNode) {
      this.tooltipElement.parentNode.removeChild(this.tooltipElement);
    }
    this.tooltipElement = undefined;
    this.unlistenEnter?.(); this.unlistenEnter = undefined;
    this.unlistenLeave?.(); this.unlistenLeave = undefined;
  }

  checkIfShouldHide() {
    if (!this.isHoveringTarget && !this.isHoveringTooltip) {
      this.hideTooltip();
    }
  }

  ngOnDestroy() {
    this.hideTooltip();
  }

  private positionTooltip(tooltipEl: HTMLElement) {
    const hostRect = this.elementRef.nativeElement.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const margin = 14;

    let top = hostRect.top - tooltipRect.height - margin;
    if (top < 0) {
      top = hostRect.bottom + margin;
    }

    let left = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - tooltipRect.width - margin));

    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.left = `${left}px`;
  }
}
